/**
 * The choreography of the point played out in "The point" section, in court feet.
 * x runs across the court (sidelines at -10 and 10), z runs down it (baselines at -22 and 22, net at 0),
 * y is height. The serving team (a1, a2) is on the near side (positive z), the camera sits behind them.
 * Pure data and math, no three.js, so it can be shared by the scene and the captions.
 */

import { clamp, lerp, smoothstep } from '@/lib/motion'

export interface Vec3 {
    x: number
    y: number
    z: number
}

export type PlayerId = 'a1' | 'a2' | 'b1' | 'b2'

/** Exaggerated about four times so the ball reads from the camera. */
export const BALL_RADIUS = 0.5

interface Segment {
    from: Vec3
    to: Vec3
    /** Highest point of the arc. */
    apex: number
    duration: number
    /** Who strikes the ball at the start of this segment. */
    hitter?: PlayerId
}

const R = BALL_RADIUS

const at = (x: number, y: number, z: number): Vec3 => ({ x, y, z })

const SPOTS = {
    serveContact: at(4.6, 1.9, 22.6),
    serveBounce: at(-4.6, R, -16.2),
    returnContact: at(-5.3, 2.2, -22.4),
    returnBounce: at(3.2, R, 16.4),
    dropContact: at(3.8, 2.0, 21.6),
    dropBounce: at(-1.6, R, -4.2),
    dinkContactB: at(-2.5, 1.1, -7.6),
    dinkBounceA: at(2.4, R, 4.1),
    dinkContactA: at(3.2, 1.1, 7.8),
    dinkBounceB: at(-2.0, R, -3.6),
    popContactB: at(-2.7, 1.0, -7.7),
    volleyContactA: at(-3.4, 5.2, 7.3),
    smashBounce: at(1.4, R, -11.0),
    pointEnd: at(3.6, 1.8, -21.5)
}

/** One entry per shot in SHOTS (constants/event.ts). */
export const CHAPTERS: Segment[][] = [
    [
        { from: SPOTS.serveContact, to: SPOTS.serveBounce, apex: 7.0, duration: 1.0, hitter: 'a1' },
        { from: SPOTS.serveBounce, to: SPOTS.returnContact, apex: 3.6, duration: 0.45 }
    ],
    [
        { from: SPOTS.returnContact, to: SPOTS.returnBounce, apex: 9.0, duration: 1.25, hitter: 'b1' },
        { from: SPOTS.returnBounce, to: SPOTS.dropContact, apex: 3.4, duration: 0.4 }
    ],
    [
        { from: SPOTS.dropContact, to: SPOTS.dropBounce, apex: 9.5, duration: 1.3, hitter: 'a1' },
        { from: SPOTS.dropBounce, to: SPOTS.dinkContactB, apex: 1.8, duration: 0.35 }
    ],
    [
        { from: SPOTS.dinkContactB, to: SPOTS.dinkBounceA, apex: 4.2, duration: 0.8, hitter: 'b1' },
        { from: SPOTS.dinkBounceA, to: SPOTS.dinkContactA, apex: 1.7, duration: 0.3 },
        { from: SPOTS.dinkContactA, to: SPOTS.dinkBounceB, apex: 4.2, duration: 0.8, hitter: 'a1' },
        { from: SPOTS.dinkBounceB, to: SPOTS.popContactB, apex: 1.6, duration: 0.3 }
    ],
    [
        { from: SPOTS.popContactB, to: SPOTS.volleyContactA, apex: 7.6, duration: 0.85, hitter: 'b1' },
        { from: SPOTS.volleyContactA, to: SPOTS.smashBounce, apex: 5.6, duration: 0.35, hitter: 'a2' },
        { from: SPOTS.smashBounce, to: SPOTS.pointEnd, apex: 2.6, duration: 0.55 }
    ]
]

const SEGMENTS: Segment[] = CHAPTERS.flat()

export const CHAPTER_TIMES: Array<{ start: number, end: number }> = (() => {
    let cursor = 0

    return CHAPTERS.map((chapter) => {
        const start = cursor
        cursor += chapter.reduce((sum, segment) => sum + segment.duration, 0)
        return { start, end: cursor }
    })
})()

export const TOTAL_TIME = CHAPTER_TIMES[CHAPTER_TIMES.length - 1].end

const SEGMENT_STARTS: number[] = (() => {
    let cursor = 0

    return SEGMENTS.map((segment) => {
        const start = cursor
        cursor += segment.duration
        return start
    })
})()

/** Moments the ball touches the court, with where it landed. */
export const BOUNCES: Array<{ time: number, x: number, z: number }> = SEGMENTS
    .map((segment, index) => ({ segment, end: SEGMENT_STARTS[index] + segment.duration }))
    .filter(({ segment }) => segment.to.y <= R + 0.001)
    .map(({ segment, end }) => ({ time: end, x: segment.to.x, z: segment.to.z }))

/** Moments a player strikes the ball. */
export const CONTACTS: Array<{ time: number, player: PlayerId }> = SEGMENTS
    .map((segment, index) => ({ segment, start: SEGMENT_STARTS[index] }))
    .filter(({ segment }) => Boolean(segment.hitter))
    .map(({ segment, start }) => ({ time: start, player: segment.hitter as PlayerId }))

/** Control height of a quadratic Bezier whose highest point is `apex`. */
const controlHeight = (from: number, to: number, apex: number): number => {
    const peak = Math.max(apex, from, to)
    return peak + Math.sqrt(Math.max(0, (peak - from) * (peak - to)))
}

/** Ball center at a moment of the point. Horizontal speed is constant within a segment, like real flight. */
export const ballAt = (time: number, target: Vec3 = { x: 0, y: 0, z: 0 }): Vec3 => {
    const clamped = clamp(time, 0, TOTAL_TIME)
    let index = SEGMENTS.length - 1

    for (let candidate = 0; candidate < SEGMENTS.length; candidate++) {
        if (clamped <= SEGMENT_STARTS[candidate] + SEGMENTS[candidate].duration) {
            index = candidate
            break
        }
    }

    const segment = SEGMENTS[index]
    const u = clamp((clamped - SEGMENT_STARTS[index]) / segment.duration)
    const control = controlHeight(segment.from.y, segment.to.y, segment.apex)

    target.x = lerp(segment.from.x, segment.to.x, u)
    target.z = lerp(segment.from.z, segment.to.z, u)
    target.y = (1 - u) * (1 - u) * segment.from.y + 2 * u * (1 - u) * control + u * u * segment.to.y

    return target
}

type PlayerKey = [chapter: number, fraction: number, x: number, z: number]

const PLAYER_KEYS: Record<PlayerId, PlayerKey[]> = {
    a1: [[0, 0, 5.0, 23.2], [1, 0.1, 5.0, 23.2], [1, 0.85, 3.9, 22.6], [2, 0.05, 3.9, 22.4], [2, 0.9, 3.6, 8.8], [3, 0.5, 3.3, 8.6], [4, 0.2, 3.0, 8.7]],
    a2: [[0, 0, -5.0, 23.2], [2, 0.05, -5.0, 23.0], [2, 0.9, -3.8, 8.8], [4, 0.1, -3.6, 8.7], [4, 0.45, -3.5, 8.4]],
    b1: [[0, 0, -5.3, -23.6], [1, 0.05, -5.3, -23.4], [1, 0.95, -3.2, -8.8], [2, 0.9, -2.7, -8.7], [3, 0.5, -2.6, -8.6], [4, 0.6, -2.8, -8.7], [4, 0.95, -2.2, -9.4]],
    b2: [[0, 0, 5.2, -8.6], [4, 0.6, 5.0, -8.6], [4, 0.9, 2.9, -9.6]]
}

export const PLAYER_IDS: PlayerId[] = ['a1', 'a2', 'b1', 'b2']

const PLAYER_TIMELINES: Record<PlayerId, Array<{ time: number, x: number, z: number }>> = Object.fromEntries(
    PLAYER_IDS.map((id) => [
        id,
        PLAYER_KEYS[id].map(([chapter, fraction, x, z]) => {
            const { start, end } = CHAPTER_TIMES[chapter]
            return { time: lerp(start, end, fraction), x, z }
        })
    ])
) as Record<PlayerId, Array<{ time: number, x: number, z: number }>>

/** Where a player stands at a moment of the point. */
export const playerAt = (id: PlayerId, time: number, target: { x: number, z: number } = { x: 0, z: 0 }): { x: number, z: number } => {
    const keys = PLAYER_TIMELINES[id]

    if (time <= keys[0].time) {
        target.x = keys[0].x
        target.z = keys[0].z
        return target
    }

    for (let index = 0; index < keys.length - 1; index++) {
        const from = keys[index]
        const to = keys[index + 1]

        if (time <= to.time) {
            const t = smoothstep(clamp((time - from.time) / Math.max(to.time - from.time, 0.0001)))
            target.x = lerp(from.x, to.x, t)
            target.z = lerp(from.z, to.z, t)
            return target
        }
    }

    const last = keys[keys.length - 1]
    target.x = last.x
    target.z = last.z
    return target
}

/** Scroll progress (0..1 across the pinned section) to chapter and point time. */
const SCROLL_START = 0.04
const SCROLL_END = 0.93
const DWELL = 0.18

export const mapPointProgress = (progress: number): { chapter: number, within: number, time: number } => {
    const span = (SCROLL_END - SCROLL_START) / CHAPTERS.length
    const local = (progress - SCROLL_START) / span
    const chapter = Math.min(Math.max(Math.floor(local), 0), CHAPTERS.length - 1)
    const within = clamp(local - chapter)
    const travel = clamp((within - DWELL) / (1 - DWELL))
    const { start, end } = CHAPTER_TIMES[chapter]

    return {
        chapter,
        within,
        time: progress <= SCROLL_START ? 0 : lerp(start, end, travel)
    }
}

/** Scroll progress at which a chapter's caption is fully in place, for the step buttons. */
export const chapterScrollProgress = (chapter: number): number => {
    const span = (SCROLL_END - SCROLL_START) / CHAPTERS.length
    return SCROLL_START + span * chapter + span * DWELL * 0.6
}
